import { useQueryClient } from '@tanstack/react-query';

export const POLL_PENDING_MARKETS_QUERY_KEY = ['poll-pending-markets'];

export interface PendingMarket {
  makerOrganizationUuid: string;
  marketUuid: string;
  takerDivisionUuid: string;
  enqueuedAt: number;
}

export const marketKey = (m: Omit<PendingMarket, 'enqueuedAt'>): string =>
  `${m.makerOrganizationUuid}:${m.takerDivisionUuid}:${m.marketUuid}`;

export const usePendingOfferPollingStore = (): {
  getPendingMarkets: () => PendingMarket[];
  addPendingMarkets: (markets: Omit<PendingMarket, 'enqueuedAt'>[]) => void;
  removePendingMarkets: (markets: Omit<PendingMarket, 'enqueuedAt'>[]) => void;
  hasPendingMarkets: () => boolean;
} => {
  const queryClient = useQueryClient();

  const getPendingMarkets = (): PendingMarket[] => {
    return queryClient.getQueryData<PendingMarket[]>(POLL_PENDING_MARKETS_QUERY_KEY) ?? [];
  };

  const addPendingMarkets = (markets: Omit<PendingMarket, 'enqueuedAt'>[]): void => {
    queryClient.setQueryData<PendingMarket[]>(POLL_PENDING_MARKETS_QUERY_KEY, (prev) => {
      const existing = prev ?? [];
      const seen = new Map(existing.map((m) => [marketKey(m), m]));
      const now = Date.now();
      for (const m of markets) {
        const key = marketKey(m);
        if (!seen.has(key)) {
          seen.set(key, { ...m, enqueuedAt: now });
        }
      }

      return Array.from(seen.values());
    });
  };

  const removePendingMarkets = (markets: Omit<PendingMarket, 'enqueuedAt'>[]): void => {
    queryClient.setQueryData<PendingMarket[]>(POLL_PENDING_MARKETS_QUERY_KEY, (prev) => {
      const existing = prev ?? [];
      const toRemove = new Set(markets.map(marketKey));
      return existing.filter((m) => !toRemove.has(marketKey(m)));
    });
  };

  const hasPendingMarkets = (): boolean => {
    return getPendingMarkets().length > 0;
  };

  return { getPendingMarkets, addPendingMarkets, removePendingMarkets, hasPendingMarkets };
};
